import { DEVICE, TYPOGRAPHY } from '../../../common/assets/styles';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  MOBILITY_EMISSION_FACTORS,
  CLASS_MULTIPLIERS,
  FLIGHT_CLASS_FACTORS,
  AIRPORT_TRANSFER_FACTORS,
  AIRPORT_DISTANCES,
  ACCOMMODATION_BASE_FACTOR,
  ACCOMMODATION_COEFFICIENTS,
  BENCHMARKING_THRESHOLDS,
  SOURCE_REFERENCES,
} from './methodology-data';

export const MethodologyContent: FC = () => {
  const { t } = useTranslation();
  const m = (key: string) => t(`common.methodology.${key}`);

  return (
    <Root>
      {/* ─── 1. Hero Header ─── */}
      <HeroSection>
        <HeroAccentBar />
        <HeroContent>
          <HeroSubtitle>{m('hero.subtitle')}</HeroSubtitle>
          <HeroDate>{m('hero.lastUpdated')}</HeroDate>
          <VerificationBadge>
            <BadgeDot />
            <span>{m('hero.verified')}</span>
          </VerificationBadge>
        </HeroContent>
      </HeroSection>

      <SectionDivider />

      {/* ─── 2. About This Calculator ─── */}
      <Section>
        <SectionTitle>{m('about.title')}</SectionTitle>
        <Paragraph>{m('about.p1')}</Paragraph>
        <Callout>
          <CalloutIcon>i</CalloutIcon>
          <CalloutText>{m('about.disclaimer')}</CalloutText>
        </Callout>
        <Paragraph>{m('about.p2')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 3. How It Works ─── */}
      <Section>
        <SectionTitle>{m('howItWorks.title')}</SectionTitle>
        <Paragraph>{m('howItWorks.p1')}</Paragraph>
        <FlowDiagram>
          <FlowBox>
            <FlowBoxLabel>{m('howItWorks.flow.activityData')}</FlowBoxLabel>
            <FlowBoxDesc>{m('howItWorks.flow.activityDataDesc')}</FlowBoxDesc>
          </FlowBox>
          <FlowArrow>×</FlowArrow>
          <FlowBox>
            <FlowBoxLabel>{m('howItWorks.flow.emissionFactor')}</FlowBoxLabel>
            <FlowBoxDesc>{m('howItWorks.flow.emissionFactorDesc')}</FlowBoxDesc>
          </FlowBox>
          <FlowArrow>=</FlowArrow>
          <FlowBoxResult>
            <FlowBoxLabel>CO₂e</FlowBoxLabel>
            <FlowBoxDesc>{m('howItWorks.flow.resultDesc')}</FlowBoxDesc>
          </FlowBoxResult>
        </FlowDiagram>
        <Paragraph>{m('howItWorks.p2')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 4. Trip Duration & Travelers ─── */}
      <Section>
        <SectionTitle>{m('tripDuration.title')}</SectionTitle>
        <Paragraph>{m('tripDuration.p1')}</Paragraph>
        <Paragraph>{m('tripDuration.p2')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 5. Travel to Monaco ─── */}
      <Section>
        <SectionTitle>{m('travelToMonaco.title')}</SectionTitle>
        <Paragraph>{m('travelToMonaco.p1')}</Paragraph>
        <Paragraph>{m('travelToMonaco.p2')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 5b. Travel Within Monaco ─── */}
      <Section>
        <SectionTitle>{m('aroundCity.title')}</SectionTitle>
        <Paragraph>{m('aroundCity.p1')}</Paragraph>

        <BaseFactorCard>
          <BaseFactorLabel>{m('aroundCity.assumption')}</BaseFactorLabel>
          <BaseFactorValue>
            5 <BaseFactorUnit>km</BaseFactorUnit>
          </BaseFactorValue>
          <BaseFactorCaption>{m('aroundCity.perPersonPerDay')}</BaseFactorCaption>
        </BaseFactorCard>

        <Paragraph>{m('aroundCity.p2')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 6. Mobility Emission Factors ─── */}
      <Section>
        <SectionTitle>{m('mobilityFactors.title')}</SectionTitle>
        <Paragraph>{m('mobilityFactors.description')}</Paragraph>

        <StyledTable>
          <thead>
            <tr>
              <TableHeader>{m('tables.modeOfTransport')}</TableHeader>
              <TableHeaderRight>{m('tables.emissionFactor')}</TableHeaderRight>
              <TableHeaderRight>{m('tables.source')}</TableHeaderRight>
            </tr>
          </thead>
          <tbody>
            {MOBILITY_EMISSION_FACTORS.map((entry) => (
              <tr key={entry.modeKey}>
                <TableCell>{m(`modes.${entry.modeKey}`)}</TableCell>
                <TableCellRight>
                  {entry.factor === 0 ? (
                    <ZeroEmissionsBadge>{m('zeroEmissions')}</ZeroEmissionsBadge>
                  ) : (
                    <>
                      <FactorValue>{entry.factor.toFixed(5)}</FactorValue>
                      <FactorUnit>{entry.unit}</FactorUnit>
                    </>
                  )}
                </TableCellRight>
                <TableCellRight>
                  {entry.source && <SourceTag>{entry.source}</SourceTag>}
                </TableCellRight>
              </tr>
            ))}
          </tbody>
        </StyledTable>

        <SubSectionTitle>{m('mobilityFactors.classMultipliers.title')}</SubSectionTitle>
        <Paragraph>{m('mobilityFactors.classMultipliers.description')}</Paragraph>

        <StyledTable>
          <thead>
            <tr>
              <TableHeader>{m('tables.flightClass')}</TableHeader>
              <TableHeaderRight>{m('tables.emissionFactor')}</TableHeaderRight>
              <TableHeaderRight>{m('tables.multiplier')}</TableHeaderRight>
              <TableHeaderRight>{m('tables.source')}</TableHeaderRight>
            </tr>
          </thead>
          <tbody>
            {FLIGHT_CLASS_FACTORS.map((entry, i) => (
              <tr key={entry.classKey}>
                <TableCell>{m(`classes.${entry.classKey}`)}</TableCell>
                <TableCellRight>
                  <FactorValue>{entry.factor.toFixed(5)}</FactorValue>
                  <FactorUnit>kg CO₂e/pkm</FactorUnit>
                </TableCellRight>
                <TableCellRight>
                  <MultiplierValue>{CLASS_MULTIPLIERS[i].multiplier}×</MultiplierValue>
                </TableCellRight>
                <TableCellRight>
                  <SourceTag>{entry.source}</SourceTag>
                </TableCellRight>
              </tr>
            ))}
          </tbody>
        </StyledTable>

        <Paragraph>{m('mobilityFactors.returnTrip')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 7. Airport Transfer Factors ─── */}
      <Section>
        <SectionTitle>{m('airportTransfer.title')}</SectionTitle>
        <Paragraph>{m('airportTransfer.description')}</Paragraph>

        <StyledTable>
          <thead>
            <tr>
              <TableHeader>{m('tables.modeOfTransport')}</TableHeader>
              <TableHeaderRight>{m('tables.emissionFactor')}</TableHeaderRight>
            </tr>
          </thead>
          <tbody>
            {AIRPORT_TRANSFER_FACTORS.map((entry) => (
              <tr key={entry.modeKey}>
                <TableCell>{m(`modes.${entry.modeKey}`)}</TableCell>
                <TableCellRight>
                  <FactorValue>{entry.factor.toFixed(5)}</FactorValue>
                  <FactorUnit>{entry.unit}</FactorUnit>
                </TableCellRight>
              </tr>
            ))}
          </tbody>
        </StyledTable>

        <DistanceNote>
          {m('airportTransfer.distanceNote')
            .replace('{roadKm}', String(AIRPORT_DISTANCES.roadKm))
            .replace('{trainKm}', String(AIRPORT_DISTANCES.trainKm))}
        </DistanceNote>
      </Section>

      <SectionDivider />

      {/* ─── 8. Accommodation Factors ─── */}
      <Section>
        <SectionTitle>{m('accommodation.title')}</SectionTitle>
        <Paragraph>{m('accommodation.description')}</Paragraph>

        <BaseFactorCard>
          <BaseFactorLabel>{m('accommodation.baseFactor')}</BaseFactorLabel>
          <BaseFactorValue>
            {ACCOMMODATION_BASE_FACTOR} <BaseFactorUnit>kg CO₂e</BaseFactorUnit>
          </BaseFactorValue>
          <BaseFactorCaption>{m('accommodation.perPersonPerNight')}</BaseFactorCaption>
        </BaseFactorCard>

        <SmallTable>
          <thead>
            <tr>
              <TableHeader>{m('tables.accommodationType')}</TableHeader>
              <TableHeaderRight>{m('tables.coefficient')}</TableHeaderRight>
            </tr>
          </thead>
          <tbody>
            {ACCOMMODATION_COEFFICIENTS.map((entry) => (
              <tr key={entry.typeKey}>
                <TableCell>{m(`accommodationTypes.${entry.typeKey}`)}</TableCell>
                <TableCellRight>
                  <MultiplierValue>{entry.coefficient}</MultiplierValue>
                </TableCellRight>
              </tr>
            ))}
          </tbody>
        </SmallTable>

        <Paragraph>{m('accommodation.formula')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 9. Emissions Benchmarking ─── */}
      <Section>
        <SectionTitle>{m('benchmarking.title')}</SectionTitle>
        <Paragraph>{m('benchmarking.description')}</Paragraph>

        <BenchmarkGrid>
          {BENCHMARKING_THRESHOLDS.map((entry) => (
            <BenchmarkCard key={entry.levelKey} $color={entry.color}>
              <BenchmarkLevel>{m(`benchmarkLevels.${entry.levelKey}`)}</BenchmarkLevel>
              <BenchmarkThreshold>{entry.threshold} t CO₂e</BenchmarkThreshold>
            </BenchmarkCard>
          ))}
        </BenchmarkGrid>

        <Paragraph>{m('benchmarking.note')}</Paragraph>
      </Section>

      <SectionDivider />

      {/* ─── 10. Sources & References ─── */}
      <Section>
        <SectionTitle>{m('sources.title')}</SectionTitle>
        <Paragraph>{m('sources.description')}</Paragraph>

        <SourceList>
          {SOURCE_REFERENCES.map((entry) => (
            <SourceItem key={entry.id}>
              <SourceNumber>{entry.id}</SourceNumber>
              <SourceContent>
                <SourceName>{m(`sourceNames.${entry.nameKey}`)}</SourceName>
                <SourceLink href={entry.url} target="_blank" rel="noopener noreferrer">
                  {entry.url}
                </SourceLink>
              </SourceContent>
            </SourceItem>
          ))}
        </SourceList>
      </Section>

      <BottomSpacer />
    </Root>
  );
};

// ─── Layout ───

const Root = styled.div`
  padding: 24px 20px;

  @media ${DEVICE.laptop} {
    padding: 32px 40px;
  }
`;

const Section = styled.section`
  margin-bottom: 8px;
`;

const SectionDivider = styled.div`
  width: 60px;
  height: 3px;
  background: var(--color2);
  border-radius: 2px;
  margin: 28px 0;

  @media ${DEVICE.laptop} {
    width: 80px;
    margin: 36px 0;
  }
`;

const BottomSpacer = styled.div`
  height: 24px;
`;

// ─── Typography ───

const SectionTitle = styled.h3`
  ${TYPOGRAPHY.titleSemibold24}
  color: var(--color6);
  margin: 0 0 16px;
  padding-left: 16px;
  border-left: 3px solid var(--color2);

  @media (max-width: 767px) {
    font-size: 20px;
    line-height: 30px;
  }
`;

const SubSectionTitle = styled.h4`
  ${TYPOGRAPHY.bodySemibold18}
  color: var(--color6);
  margin: 28px 0 12px;
`;

const Paragraph = styled.p`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color6);
  margin: 0 0 16px;
  line-height: 1.7;

  @media (max-width: 767px) {
    font-size: 14px;
    line-height: 1.65;
  }
`;

// ─── Hero Section ───

const HeroSection = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
`;

const HeroAccentBar = styled.div`
  width: 4px;
  background: var(--color2);
  border-radius: 2px;
  flex-shrink: 0;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeroSubtitle = styled.p`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color9);
  margin: 0;
`;

const HeroDate = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color7);
`;

const VerificationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(74, 140, 94, 0.08);
  border: 1px solid rgba(74, 140, 94, 0.2);
  border-radius: 20px;
  width: fit-content;

  & > span {
    ${TYPOGRAPHY.captionMedium12}
    color: var(--color13);
  }
`;

const BadgeDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color13);
`;

// ─── Callout ───

const Callout = styled.div`
  display: flex;
  gap: 14px;
  padding: 16px 20px;
  background: var(--color14);
  border-radius: 12px;
  border-left: 3px solid var(--color2);
  margin: 16px 0 20px;
`;

const CalloutIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color2);
  color: var(--color5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
`;

const CalloutText = styled.p`
  ${TYPOGRAPHY.bodyRegular14}
  color: var(--color6);
  margin: 0;
  line-height: 1.6;
`;

// ─── Flow Diagram ───

const FlowDiagram = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 24px 0 28px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FlowBox = styled.div`
  flex: 1;
  min-width: 120px;
  max-width: 200px;
  padding: 16px;
  background: var(--color14);
  border: 1px solid var(--color10);
  border-radius: 12px;
  text-align: center;

  @media (max-width: 600px) {
    max-width: 100%;
    width: 100%;
  }
`;

const FlowBoxLabel = styled.div`
  ${TYPOGRAPHY.bodySemibold18}
  color: var(--color6);
  margin-bottom: 4px;
`;

const FlowBoxDesc = styled.div`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
`;

const FlowBoxResult = styled(FlowBox)`
  background: var(--color2);
  border-color: var(--color1);

  ${FlowBoxLabel}, ${FlowBoxDesc} {
    color: var(--color5);
  }
`;

const FlowArrow = styled.div`
  ${TYPOGRAPHY.titleSemibold24}
  color: var(--color2);
  flex-shrink: 0;

  @media (max-width: 600px) {
    transform: rotate(90deg);
    margin: -4px 0;
  }
`;

// ─── Tables ───

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--color10);

  & tbody tr:nth-child(even) {
    background: var(--color14);
  }
  & tbody tr:hover {
    background: rgba(206, 17, 38, 0.03);
  }
`;

const SmallTable = styled(StyledTable)`
  max-width: 480px;
`;

const TableHeader = styled.th`
  ${TYPOGRAPHY.captionMedium12}
  text-align: left;
  padding: 12px 16px;
  background: var(--color11);
  color: var(--color5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableHeaderRight = styled(TableHeader)`
  text-align: right;
`;

const TableCell = styled.td`
  ${TYPOGRAPHY.bodyRegular14}
  padding: 12px 16px;
  color: var(--color6);
  border-top: 1px solid var(--color10);
`;

const TableCellRight = styled(TableCell)`
  text-align: right;
`;

const FactorValue = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  font-variant-numeric: tabular-nums;
`;

const FactorUnit = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  margin-left: 6px;
`;

const SourceTag = styled.span`
  ${TYPOGRAPHY.captionRegular10}
  color: var(--color9);
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 8px;
  border-radius: 4px;
`;

const ZeroEmissionsBadge = styled.span`
  ${TYPOGRAPHY.captionMedium12}
  color: var(--color13);
  background: rgba(74, 140, 94, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
`;

const MultiplierValue = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color2);
  font-variant-numeric: tabular-nums;
`;

// ─── Distance Note ───

const DistanceNote = styled.p`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  margin: 8px 0 0;
  padding: 12px 16px;
  background: var(--color14);
  border-radius: 8px;
  line-height: 1.6;
`;

// ─── Accommodation Card ───

const BaseFactorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, var(--color14) 0%, rgba(206, 17, 38, 0.04) 100%);
  border: 1px solid var(--color10);
  border-radius: 16px;
  margin: 20px 0;
  text-align: center;
`;

const BaseFactorLabel = styled.span`
  ${TYPOGRAPHY.captionMedium12}
  color: var(--color9);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const BaseFactorValue = styled.div`
  ${TYPOGRAPHY.titleSemibold36}
  color: var(--color2);
`;

const BaseFactorUnit = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color9);
`;

const BaseFactorCaption = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  margin-top: 4px;
`;

// ─── Benchmarking ───

const BenchmarkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 20px 0;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const BenchmarkCard = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  border-radius: 12px;
  border: 2px solid ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}0D`};
  text-align: center;
`;

const BenchmarkLevel = styled.span`
  ${TYPOGRAPHY.bodySemibold18}
  color: var(--color6);
  margin-bottom: 4px;
`;

const BenchmarkThreshold = styled.span`
  ${TYPOGRAPHY.captionMedium12}
  color: var(--color9);
`;

// ─── Sources ───

const SourceList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SourceItem = styled.li`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const SourceNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color14);
  border: 1px solid var(--color10);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${TYPOGRAPHY.captionMedium12}
  color: var(--color2);
`;

const SourceContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SourceName = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
`;

const SourceLink = styled.a`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color2);
  text-decoration: none;
  word-break: break-all;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
    text-decoration: underline;
  }
`;
